"use client";

import Header from "@/app/homepage/layouts/Header";
import React, { createContext, useEffect, useRef, useState } from "react";
import FloatButton from "@/components/Floating/FloatButton";
import FloatingContent from "@/components/Floating/FloatingContent";
import Footer from "@/app/homepage/layouts/Footer";
import PageInformation from "@/app/homepage/layouts/PageInformation";
import {usePathname, useRouter, useSearchParams} from "next/navigation";
import SideNav from "@/app/homepage/layouts/SideNav";
import { useStore } from "@/store";
import { observer } from "mobx-react";
import Modal from "@/components/modal";
import Script from "next/script";
import styled from "styled-components";
import PageDepthsInformation from "@/app/homepage/layouts/PageDepthsInformation";
import MainPopupWrap from "@/app/homepage/main/component/MainPopupWrap";
import { SIZES } from "@/components/constants";
import {useMediaQuery} from "react-responsive";
import Loading from "@/components/common/Loading";
import {setGlobalUseRouter} from "@/utils";

export const LayoutContext = createContext(null);
const NAVER_CLIENT_ID = process.env.NEXT_PUBLIC_NAVER_MAP_CLIENT_ID;

const ClientRootHomepage = observer(({ children }) => {
    const pathname = usePathname();
    const searchParams = useSearchParams();
    const router = useRouter();
    const queryString = searchParams.toString();
    const isHomepageRoot = pathname === "/homepage";
    const isHomepageSearchRoot = pathname === "/homepage/search";
    const isHomepageBoardDetail = pathname === "/homepage/management/detail";
    const hideFooter = pathname.indexOf("/cooperation") > -1;
    const rootRef = useRef(null);
    const footerStopRef = useRef(null);
    const footerRef = useRef(null);
    const [loaded, setLoaded] = useState(false);
    const isTablet = useMediaQuery({
        query: `(max-width: ${SIZES.TABLET})`
    });
    setGlobalUseRouter(router);

    useEffect(() => {
        const handleLoad = () => setLoaded(true);

        if (document.readyState === "complete") {
            handleLoad();
        } else {
            window.addEventListener("load", handleLoad);
        }

        return () => window.removeEventListener("load", handleLoad);
    }, []);

    useEffect(() => {
        if (rootRef.current) rootRef.current.scrollTop = 0;
    }, [pathname]);

    useEffect(() => {
        if (pathname.indexOf("/cooperation") > -1) return;
        if (rootRef.current && !hideFooter) rootRef.current.scrollTop = 0;
    }, [queryString, hideFooter, pathname]);

    return (
        <LayoutContext.Provider value={{ rootRef }}>
            <HomePageRootWrap className={` ${!isHomepageRoot ? "page" : ""}`} ref={rootRef}>
                {!loaded && (
                    <LoadingContainer>
                        <div className="lds-ring">
                            <div></div>
                            <div></div>
                            <div></div>
                            <div></div>
                        </div>
                    </LoadingContainer>
                )}
                <Loading />

                <Script
                    src={`https://oapi.map.naver.com/openapi/v3/maps.js?ncpKeyId=${NAVER_CLIENT_ID}&submodules=geocoder`}
                    strategy="afterInteractive"
                    onReady={() => {
                        window.__NAVER_MAPS_READY__ = true;
                        window.dispatchEvent(new Event("naver-maps-ready"));
                    }}
                />

                <Header scrollRef={rootRef} className={`${!isHomepageRoot ? "page" : ""}`} />

                <div className="homepage_wrapper">
                    {!isHomepageRoot && !isHomepageSearchRoot && !isHomepageBoardDetail && (
                        <>
                            <PageInformation scrollRef={rootRef} />
                            <PageDepthsInformation scrollRef={rootRef} />
                        </>
                    )}

                    {children}

                    {!isHomepageSearchRoot && (
                        <>
                            <FloatButton scrollRef={rootRef} footerObs={footerStopRef} className={`${!isHomepageRoot ? "page" : ""}`}/>
                            <FloatingContent />
                        </>
                    )}
                </div>

                {
                    (!isTablet || !hideFooter) &&
                    <>
                        <div ref={footerStopRef} className="footer-stop-sensor" />
                        <Footer ref={footerRef} />
                    </>
                }

                <SideNav />
                <Modal />
                {isHomepageRoot && <MainPopupWrap />}
            </HomePageRootWrap>
        </LayoutContext.Provider>
    );
});

const HomePageRootWrap = styled.div`
    position: relative;
    overflow-y: auto;
    height: calc(var(--vh) * 100);

    &.page {
        height: calc((var(--vh) * 100) - 96px);
        margin-top: 96px;
    }

    @media (max-width: ${SIZES.HEADER}) {
        &.page {
            height: calc((var(--vh) * 100) - 64px);
            margin-top: 64px;
        }
    }

    .homepage_wrapper {
        position: relative;
    }
    
    .footer-stop-sensor {
        height: 1px;
    }
`;

const LoadingContainer = styled.div`
    position: fixed;
    inset: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.75);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 999999999;
    pointer-events: auto;

    .lds-ring {
        display: inline-block;
        position: relative;
        width: 80px;
        height: 80px;
    }

    .lds-ring div {
        box-sizing: border-box;
        display: block;
        position: absolute;
        width: 64px;
        height: 64px;
        margin: 8px;
        border: 8px solid #fff;
        border-radius: 50%;
        animation: lds-ring 1.2s cubic-bezier(0.5, 0, 0.5, 1) infinite;
        border-color: #fff transparent transparent transparent;
    }

    .lds-ring div:nth-child(1) {
        animation-delay: -0.45s;
    }

    .lds-ring div:nth-child(2) {
        animation-delay: -0.3s;
    }

    .lds-ring div:nth-child(3) {
        animation-delay: -0.15s;
    }

    @keyframes lds-ring {
        0% {
            transform: rotate(0deg);
        }
        100% {
            transform: rotate(360deg);
        }
    }
`;

export default ClientRootHomepage;
